/**
 * Pure routing-rule evaluation shared by the editor (preview, warnings), the "Who gets
 * notified" summary and the public enquiry form (to predict a meeting offer).
 *
 * Mirrors `EnquiryFormRoutingRule::matches()` in PHP: a rule applies when every non-empty
 * condition list contains the enquiry's value, and the first applying rule in order wins.
 * The server is authoritative: `createEnquiry` returns where an enquiry was actually routed.
 */

import type {
    DestinationValue,
    EnquirerTypeValue,
    FieldCondition,
    ProjectKindValue,
} from '../types';

/**
 * Every destination the server accepts on a rule or form default. `Branch`
 * hands the enquiry to a branch in the owner's organisation network (not on
 * Website forms). Kept as an alias of the editor's `DestinationValue`, which
 * includes it.
 */
export type RoutingDestinationValue = DestinationValue;

/** The conditions and outcome of a rule; recipients are not needed to evaluate. */
export interface RoutingRuleConditions {
    enquiryFormTypeIds: number[];
    enquirerTypes: EnquirerTypeValue[];
    projectKinds: ProjectKindValue[];
    countries: string[];
    regions: string[];
    /** Conditions on custom Select fields (Website forms). */
    fieldConditions: FieldCondition[];
    /** Branch professional IDs the visitor may have picked; empty or absent matches any. */
    branchIds?: number[];
    destination: DestinationValue;
    /** The branch that receives the enquiry when `destination` is `Branch`. */
    destinationBranchId?: number | null;
}

/** What is known about an enquiry when routing it. `null` means not answered. */
export interface RoutingInput {
    typeId: number | null;
    enquirerType: EnquirerTypeValue | null;
    projectKind: ProjectKindValue | null;
    /** ISO 3166-1 alpha-2, e.g. NZ. */
    country: string | null;
    /** Region URL segment, or a slug of the region name. */
    regionSegment: string | null;
    /** Custom field answers by key; a missing key means not answered. */
    fieldAnswers?: Record<string, string>;
    /** The branch picked on a form that asks which branch; absent or null means none. */
    branchId?: number | null;
}

export type RoutingDimension =
    | 'enquiryType'
    | 'enquirerType'
    | 'projectKind'
    | 'country'
    | 'region'
    | 'branch'
    | 'fields';

export const ROUTING_DIMENSIONS: readonly RoutingDimension[] = [
    'enquiryType',
    'enquirerType',
    'projectKind',
    'country',
    'region',
    'branch',
    'fields',
];

export function ruleMatches(
    rule: RoutingRuleConditions,
    input: RoutingInput,
): boolean {
    if (
        rule.enquiryFormTypeIds.length > 0 &&
        (input.typeId === null ||
            !rule.enquiryFormTypeIds.includes(input.typeId))
    ) {
        return false;
    }
    if (
        rule.enquirerTypes.length > 0 &&
        (input.enquirerType === null ||
            !rule.enquirerTypes.includes(input.enquirerType))
    ) {
        return false;
    }
    if (
        rule.projectKinds.length > 0 &&
        (input.projectKind === null ||
            !rule.projectKinds.includes(input.projectKind))
    ) {
        return false;
    }
    if (rule.countries.length > 0) {
        const country = (input.country ?? '').toUpperCase();
        if (!rule.countries.some((code) => code.toUpperCase() === country)) {
            return false;
        }
    }
    if (rule.regions.length > 0) {
        const region = (input.regionSegment ?? '').toLowerCase();
        if (!rule.regions.some((segment) => segment.toLowerCase() === region)) {
            return false;
        }
    }
    const branchIds = rule.branchIds ?? [];
    if (
        branchIds.length > 0 &&
        (input.branchId === null ||
            input.branchId === undefined ||
            !branchIds.includes(input.branchId))
    ) {
        return false;
    }
    for (const condition of rule.fieldConditions) {
        if (condition.values.length === 0) continue;
        const answer = (input.fieldAnswers?.[condition.key] ?? '').trim();
        if (!condition.values.includes(answer)) return false;
    }
    return true;
}

/** Index of the first rule that applies, or -1 for the default destination. */
export function firstMatchingRuleIndex(
    rules: readonly RoutingRuleConditions[],
    input: RoutingInput,
): number {
    return rules.findIndex((rule) => ruleMatches(rule, input));
}

/** Whether the rule has any condition at all. A condition-less rule catches everything. */
export function ruleHasConditions(rule: RoutingRuleConditions): boolean {
    return ROUTING_DIMENSIONS.some((dimension) =>
        ruleUsesDimension(rule, dimension),
    );
}

export function ruleUsesDimension(
    rule: RoutingRuleConditions,
    dimension: RoutingDimension,
): boolean {
    switch (dimension) {
        case 'enquiryType':
            return rule.enquiryFormTypeIds.length > 0;
        case 'enquirerType':
            return rule.enquirerTypes.length > 0;
        case 'projectKind':
            return rule.projectKinds.length > 0;
        case 'country':
            return rule.countries.length > 0;
        case 'region':
            return rule.regions.length > 0;
        case 'branch':
            return (rule.branchIds ?? []).length > 0;
        case 'fields':
            return rule.fieldConditions.some((c) => c.values.length > 0);
        default: {
            const exhaustive: never = dimension;
            return exhaustive;
        }
    }
}

/**
 * Whether rule `index` can never be reached because an earlier rule matches every enquiry
 * this one would. Set-wise: every earlier rule's condition lists are subsets of this one's
 * (an empty earlier list matches anything, so it is always a subset).
 */
export function ruleIsShadowed(
    rules: readonly RoutingRuleConditions[],
    index: number,
): boolean {
    const rule = rules[index];
    if (!rule) return false;
    return rules.slice(0, index).some((earlier) => covers(earlier, rule));
}

/** `outer` matches every enquiry `inner` matches. */
function covers(
    outer: RoutingRuleConditions,
    inner: RoutingRuleConditions,
): boolean {
    return (
        subset(outer.enquiryFormTypeIds, inner.enquiryFormTypeIds) &&
        subset(outer.enquirerTypes, inner.enquirerTypes) &&
        subset(outer.projectKinds, inner.projectKinds) &&
        subset(outer.countries.map(upper), inner.countries.map(upper)) &&
        subset(outer.regions.map(lower), inner.regions.map(lower)) &&
        subset(outer.branchIds ?? [], inner.branchIds ?? []) &&
        fieldConditionsCover(outer.fieldConditions, inner.fieldConditions)
    );
}

/** Every outer field condition is satisfied by a matching inner condition that is its subset. */
function fieldConditionsCover(
    outer: readonly FieldCondition[],
    inner: readonly FieldCondition[],
): boolean {
    return outer
        .filter((c) => c.values.length > 0)
        .every((o) => {
            const i = inner.find((c) => c.key === o.key);
            return i !== undefined && subset(o.values, i.values);
        });
}

/**
 * An empty `outer` list matches anything. A non-empty `outer` covers `inner` only when
 * `inner` is non-empty and every one of its values is in `outer`.
 */
function subset<T>(outer: readonly T[], inner: readonly T[]): boolean {
    if (outer.length === 0) return true;
    if (inner.length === 0) return false;
    return inner.every((value) => outer.includes(value));
}

const upper = (value: string) => value.toUpperCase();
const lower = (value: string) => value.toLowerCase();

/** Same slug the backend falls back to when a region name has no Region row. */
export function regionSlug(value: string | null | undefined): string | null {
    const slug = (value ?? '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    return slug === '' ? null : slug;
}
